import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  cancelExternalAppPromptRun,
  captureExternalAppEvidence,
  deleteExternalAppPromptRule,
  getExternalAppPromptRun,
  getLatestExternalAppPromptRun,
  getExternalAppEvidenceScreenshot,
  saveExternalAppPromptRule,
  sendExternalAppCalibrationAction,
  startExternalAppPromptRun,
  testExternalAppPromptRuleDetection,
  testExternalAppPromptRuleResponse,
  updateExternalAppPromptRule,
} from './externalAppControlApi';
import {
  buildVisualTemplateFromImage,
  normalizedPointFromImage,
} from './visualTemplateSelection';

export const PROMPT_PRESETS = {
  skip_offered_advert: {
    label: 'Offered Skip control',
    initialDelayMs: 1000,
    intervalMs: 3000,
    windowMs: 45000,
    continueAfterResponseMs: 15000,
    maxOccurrences: 2,
  },
  profile_picker: {
    label: 'Profile picker',
    initialDelayMs: 1000,
    intervalMs: 2000,
    windowMs: 15000,
    continueAfterResponseMs: 0,
    maxOccurrences: 1,
  },
  resume_prompt: {
    label: 'Resume or confirmation prompt',
    initialDelayMs: 1000,
    intervalMs: 2000,
    windowMs: 10000,
    continueAfterResponseMs: 0,
    maxOccurrences: 1,
  },
  buffering_indicator: {
    label: 'Buffering indicator',
    initialDelayMs: 1000,
    intervalMs: 3000,
    windowMs: 15000,
    continueAfterResponseMs: 0,
    maxOccurrences: 1,
  },
  ready_screen: {
    label: 'App ready screen',
    initialDelayMs: 500,
    intervalMs: 2000,
    windowMs: 120000,
    continueAfterResponseMs: 0,
    maxOccurrences: 1,
  },
  other: {
    label: 'Other prompt',
    initialDelayMs: 1000,
    intervalMs: 3000,
    windowMs: 15000,
    continueAfterResponseMs: 0,
    maxOccurrences: 1,
  },
};

const DEFAULT_SELECTION_THRESHOLD = 0.86;

const emptyForm = (packageName) => ({
  id: null,
  name: '',
  enabled: false,
  promptType: 'skip_offered_advert',
  scope: { packageName },
  triggers: ['after_package_launch', 'after_intent_launch', 'manual'],
  observation: { ...PROMPT_PRESETS.skip_offered_advert },
  decision: { minimumConfidence: DEFAULT_SELECTION_THRESHOLD },
  response: { steps: [] },
  accessibilityValues: '',
  visualTemplate: null,
  sourceEvidenceId: null,
});

export const getPromptSelectionWarning = (selection) => {
  if (!selection) return 'Draw a rectangle around the stable prompt text or control.';
  const area = selection.width * selection.height;
  if (selection.width < 0.02 || selection.height < 0.02 || area < 0.001) {
    return 'This crop is very small. Include enough of the stable label to match reliably.';
  }
  if (selection.width > 0.5 || selection.height > 0.4 || area > 0.15) {
    return 'This crop is very large. Exclude moving video, countdowns, and unrelated controls.';
  }
  return '';
};

export const buildPromptRulePayload = (form, visualTemplate, controlTransport = 'accessibility') => {
  const accessibilityValues = String(form.accessibilityValues || '')
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);
  const detectors = [];
  if (visualTemplate) {
    detectors.push({
      type: 'visual_template',
      required: true,
      weight: 1,
      template: visualTemplate,
    });
  }
  if (accessibilityValues.length) {
    detectors.push({
      type: 'accessibility_text',
      required: !visualTemplate,
      weight: visualTemplate ? 0.6 : 1,
      mode: 'one_of',
      values: accessibilityValues,
    });
  }
  return {
    name: form.name,
    promptType: form.promptType,
    enabled: form.enabled === true && (
      form.promptType === 'ready_screen'
      || (
        form.promptType !== 'buffering_indicator'
        && form.response?.steps?.length > 0
      )
    ),
    scope: form.scope,
    triggers: form.triggers,
    observation: {
      initialDelayMs: Number(form.observation.initialDelayMs),
      intervalMs: Number(form.observation.intervalMs),
      windowMs: Number(form.observation.windowMs),
      continueAfterResponseMs: Number(form.observation.continueAfterResponseMs),
      maxOccurrences: Number(form.observation.maxOccurrences),
    },
    detectors,
    decision: {
      minimumConfidence: Number(form.decision.minimumConfidence),
      minimumPositiveChecks: 1,
      maximumEvidenceAgeMs: 2000,
    },
    response: {
      transport: controlTransport,
      steps: Array.isArray(form.response?.steps)
        ? form.response.steps.map((step) => ({
            type: 'remote_action',
            key: step.key,
            delayAfterMs: Number(step.delayAfterMs),
          }))
        : [],
    },
    createdFrom: form.sourceEvidenceId
      ? {
          evidenceId: form.sourceEvidenceId,
          capturedAt: new Date().toISOString(),
        }
      : null,
  };
};

function detectorSummary(rule) {
  const visual = rule.detectors?.find((detector) => detector.type === 'visual_template');
  const accessibility = rule.detectors?.find(
    (detector) => detector.type === 'accessibility_text'
  );
  return {
    visual,
    accessibility,
    label: [
      visual ? 'visual crop' : null,
      accessibility ? 'accessibility text' : null,
    ].filter(Boolean).join(' + '),
  };
}

function PromptDetectionPanel({
  session,
  packageName,
  appLabel,
  workingActions = [],
  disabled = false,
  onMessage,
  onError,
  simple = false,
  controlTransport = 'accessibility',
  onRemoteAction,
  captureRequest = 0,
  onRulesChanged,
}) {
  const [rules, setRules] = useState([]);
  const [form, setForm] = useState(() => emptyForm(packageName));
  const [capture, setCapture] = useState(null);
  const [selection, setSelection] = useState(null);
  const [busy, setBusy] = useState('');
  const [testResults, setTestResults] = useState({});
  const [activeRun, setActiveRun] = useState(null);
  const [responseRecordMode, setResponseRecordMode] = useState('send_and_record');
  const imageRef = useRef(null);
  const dragStartRef = useRef(null);
  const lastCaptureRequestRef = useRef(captureRequest);

  useEffect(() => {
    const restored = Array.isArray(session?.promptRules)
      ? session.promptRules
      : Array.isArray(session?.draftProfile?.promptRules)
        ? session.draftProfile.promptRules
        : [];
    setRules(restored);
    setForm(emptyForm(packageName));
    setCapture(null);
    setSelection(null);
  }, [packageName, session?.id, session?.promptRules, session?.draftProfile?.promptRules]);

  useEffect(() => {
    if (!activeRun?.id || activeRun.status !== 'running') return undefined;
    let cancelled = false;
    const poll = async () => {
      try {
        const next = await getExternalAppPromptRun(activeRun.id);
        if (cancelled || !next) return;
        setActiveRun(next);
        if (next.status !== 'running') {
          const cleared = next.result?.occurrences?.filter((entry) => entry.cleared).length || 0;
          onMessage(
            next.status === 'cleared'
              ? `Prompt watch completed and cleared ${cleared} occurrence${cleared === 1 ? '' : 's'}.`
              : `Prompt watch finished: ${next.status.replace(/_/g, ' ')}.`
          );
        }
      } catch (error) {
        if (!cancelled) {
          onError(error.response?.data?.error || error.message || 'Failed to refresh prompt watch');
        }
      }
    };
    const timer = window.setInterval(poll, 1000);
    poll();
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [activeRun?.id, activeRun?.status, onError, onMessage]);

  const selectionWarning = useMemo(
    () => getPromptSelectionWarning(selection),
    [selection]
  );
  const hasAutomaticRules = rules.some((rule) =>
    rule.enabled === true && rule.response?.steps?.length > 0
  );

  useEffect(() => {
    if (!session?.id || !hasAutomaticRules) return undefined;
    let cancelled = false;
    const loadLatest = async () => {
      try {
        const latest = await getLatestExternalAppPromptRun(session.id);
        if (!cancelled && latest) {
          setActiveRun((current) => (
            !current || latest.id >= current.id ? latest : current
          ));
        }
      } catch (error) {
        if (!cancelled) {
          onError(
            error.response?.data?.error
            || error.message
            || 'Failed to load automatic prompt status'
          );
        }
      }
    };
    const timer = window.setInterval(loadLatest, 2000);
    loadLatest();
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [hasAutomaticRules, onError, session?.id]);

  const run = async (key, operation) => {
    setBusy(key);
    onError('');
    try {
      return await operation();
    } catch (error) {
      onError(
        error.response?.data?.error
        || error.response?.data?.message
        || error.message
        || 'Prompt setup failed'
      );
      return null;
    } finally {
      setBusy('');
    }
  };

  const updateObservation = (key, value) => {
    setForm((current) => ({
      ...current,
      observation: {
        ...current.observation,
        [key]: value,
      },
    }));
  };

  const applyPreset = (promptType) => {
    const preset = PROMPT_PRESETS[promptType] || PROMPT_PRESETS.other;
    setForm((current) => ({
      ...current,
      promptType,
      enabled: promptType === 'buffering_indicator' ? false : current.enabled,
      name: current.id || current.name ? current.name : preset.label,
      observation: {
        initialDelayMs: preset.initialDelayMs,
        intervalMs: preset.intervalMs,
        windowMs: preset.windowMs,
        continueAfterResponseMs: preset.continueAfterResponseMs,
        maxOccurrences: preset.maxOccurrences,
      },
    }));
  };

  const handleCapture = async (captureType = null) => {
    const result = await run('capture', async () => {
      const evidence = await captureExternalAppEvidence(session.id, true, 3000);
      const screenshot = await getExternalAppEvidenceScreenshot(
        session.id,
        evidence.evidenceId
      );
      if (!screenshot?.dataUrl) {
        throw new Error('The device returned evidence without a screenshot.');
      }
      return { evidence, dataUrl: screenshot.dataUrl };
    });
    if (!result) return;
    setCapture(result);
    setSelection(null);
    setForm((current) => ({
      ...current,
      ...(captureType ? {
        name: captureType === 'ready_screen' ? `${appLabel} ready screen` : '',
        promptType: captureType,
        enabled: captureType === 'ready_screen',
        observation: { ...(PROMPT_PRESETS[captureType] || PROMPT_PRESETS.other) },
      } : {}),
      visualTemplate: null,
      sourceEvidenceId: result.evidence.evidenceId,
    }));
    onMessage('Prompt captured. Draw a tight rectangle around its stable text or control.');
  };

  useEffect(() => {
    if (captureRequest === lastCaptureRequestRef.current) return;
    lastCaptureRequestRef.current = captureRequest;
    handleCapture(simple ? 'profile_picker' : null);
  }, [captureRequest]);

  const pointFromEvent = (event) => {
    return normalizedPointFromImage(event.clientX, event.clientY, imageRef.current);
  };

  const handlePointerDown = (event) => {
    const point = pointFromEvent(event);
    if (!point) return;
    dragStartRef.current = point;
    setSelection({ ...point, width: 0, height: 0 });
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handlePointerMove = (event) => {
    if (!dragStartRef.current) return;
    const start = dragStartRef.current;
    const point = pointFromEvent(event);
    if (!point) return;
    setSelection({
      x: Math.min(start.x, point.x),
      y: Math.min(start.y, point.y),
      width: Math.abs(point.x - start.x),
      height: Math.abs(point.y - start.y),
    });
  };

  const handlePointerUp = () => {
    dragStartRef.current = null;
    setSelection((current) => (
      current?.width >= 0.01 && current?.height >= 0.01 ? current : null
    ));
  };

  const makeVisualTemplate = () => {
    if (!capture?.dataUrl || !selection || !imageRef.current) {
      return form.visualTemplate
        ? {
            ...form.visualTemplate,
            threshold: Number(form.decision.minimumConfidence),
          }
        : null;
    }
    return buildVisualTemplateFromImage(
      imageRef.current,
      selection,
      form.decision.minimumConfidence
    );
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      onError('Name this screen before saving it. For example: YouTube profile picker.');
      return;
    }
    if (
      form.enabled
      && !['ready_screen', 'buffering_indicator'].includes(form.promptType)
      && form.response.steps.length === 0
    ) {
      onError('Record at least one button press before enabling automatic handling.');
      return;
    }
    if (selection && selectionWarning) {
      onError(selectionWarning);
      return;
    }
    const saved = await run('save', async () => {
      const visualTemplate = makeVisualTemplate();
      const payload = buildPromptRulePayload(form, visualTemplate, controlTransport);
      if (!payload.detectors.length) {
        throw new Error('Capture a visual crop or add accessibility text before saving.');
      }
      return form.id
        ? updateExternalAppPromptRule(session.id, form.id, payload)
        : saveExternalAppPromptRule(session.id, payload);
    });
    if (!saved) return;
    setRules((current) => [
      ...current.filter((rule) => rule.id !== saved.id),
      saved,
    ]);
    setForm(emptyForm(packageName));
    setCapture(null);
    setSelection(null);
    onRulesChanged?.();
    onMessage(
      simple
        ? `${saved.name} saved${saved.enabled ? ' and available to automatic startup' : ''}.`
        : `${saved.name} saved${saved.enabled ? ' and enabled for automatic responses' : ' as a manual-only rule'}.`
    );
  };

  const handleEdit = (rule) => {
    const summary = detectorSummary(rule);
    setForm({
      id: rule.id,
      name: rule.name,
      enabled: rule.enabled === true,
      promptType: rule.promptType,
      scope: rule.scope,
      triggers: rule.triggers,
      observation: rule.observation,
      decision: rule.decision,
      response: {
        steps: Array.isArray(rule.response?.steps)
          ? rule.response.steps.map((step) => ({ ...step }))
          : [],
      },
      accessibilityValues: summary.accessibility?.values?.join(', ') || '',
      visualTemplate: summary.visual?.template || null,
      sourceEvidenceId: rule.createdFrom?.evidenceId || null,
    });
    setCapture(null);
    setSelection(null);
    onMessage(`Editing ${rule.name}. Capture again only if you want to replace its visual crop.`);
  };

  const handleTest = async (rule) => {
    const result = await run(`test:${rule.id}`, () =>
      testExternalAppPromptRuleDetection(session.id, rule.id)
    );
    if (!result) return;
    setTestResults((current) => ({ ...current, [rule.id]: result }));
    onMessage(
      result.matched
        ? `${rule.name} is visible (${Math.round(result.confidence * 100)}% match). No button was pressed.`
        : `${rule.name} is not visible. No button was pressed.`
    );
  };

  const handleRecordResponseStep = async (action) => {
    const shouldSend = responseRecordMode !== 'record_only';
    const shouldRecord = responseRecordMode !== 'send_only';
    if (shouldSend) {
      const result = await run(`record-response:${action}`, () => (
        controlTransport === 'google_tv' && onRemoteAction
          ? onRemoteAction(action)
          : sendExternalAppCalibrationAction(session.id, action, controlTransport)
      ));
      if (!result) return;
    }
    if (shouldRecord) {
      setForm((current) => ({
        ...current,
        ...(simple ? { enabled: true } : {}),
        response: {
          steps: [
            ...(current.response?.steps || []),
            {
              type: 'remote_action',
              key: action,
              delayAfterMs: 500,
            },
          ].slice(0, 10),
        },
      }));
    }
    const label = action.replace(/_/g, ' ');
    onMessage(
      shouldSend && shouldRecord
        ? `${label} sent to the TV and added to the response.`
        : shouldSend
          ? `${label} sent to the TV without changing the recorded response.`
          : `${label} added to the response without controlling the TV.`
    );
  };

  const handleResponseDelay = (index, value) => {
    setForm((current) => ({
      ...current,
      response: {
        steps: current.response.steps.map((step, stepIndex) => (
          stepIndex === index
            ? { ...step, delayAfterMs: value }
            : step
        )),
      },
    }));
  };

  const handleRemoveResponseStep = (index) => {
    setForm((current) => ({
      ...current,
      response: {
        steps: current.response.steps.filter((step, stepIndex) => stepIndex !== index),
      },
    }));
  };

  const handleTestResponse = async (rule) => {
    if (!window.confirm(
      `Test "${rule.name}" now? The saved response will control the TV only if the prompt matches.`
    )) return;
    const result = await run(`test-response:${rule.id}`, () =>
      testExternalAppPromptRuleResponse(session.id, rule.id)
    );
    if (!result) return;
    setTestResults((current) => ({
      ...current,
      [rule.id]: { ...result, testKind: 'response' },
    }));
    onMessage(
      !result.actionSent
        ? `${rule.name} did not match, so no response was sent.`
        : result.cleared
          ? `${rule.name} matched, the response was sent, and the prompt cleared.`
          : `${rule.name} matched and the response was sent, but clearing could not be verified.`
    );
  };

  const handleDelete = async (rule) => {
    if (!window.confirm(`Delete "${rule.name}" from this setup draft?`)) return;
    const result = await run(`delete:${rule.id}`, () =>
      deleteExternalAppPromptRule(session.id, rule.id)
    );
    if (!result) return;
    setRules((current) => current.filter((entry) => entry.id !== rule.id));
    onRulesChanged?.();
    if (form.id === rule.id) setForm(emptyForm(packageName));
    onMessage(`${rule.name} deleted from the setup draft.`);
  };

  const handleStartWatch = async () => {
    const started = await run('start-watch', () =>
      startExternalAppPromptRun(session.id, 'manual')
    );
    if (!started) return;
    setActiveRun(started);
    onMessage('Watching the current app for trained prompts.');
  };

  const handleStopWatch = async () => {
    if (!activeRun?.id) return;
    const result = await run('stop-watch', () =>
      cancelExternalAppPromptRun(activeRun.id)
    );
    if (!result) return;
    setActiveRun((current) => current ? { ...current, status: 'cancelled' } : current);
    onMessage('Prompt watch cancellation requested.');
  };

  const toggleTrigger = (trigger) => {
    setForm((current) => {
      const selected = current.triggers.includes(trigger);
      const triggers = selected
        ? current.triggers.filter((entry) => entry !== trigger)
        : [...current.triggers, trigger];
      return { ...current, triggers: triggers.length ? triggers : ['manual'] };
    });
  };

  return (
    <div className={`apps-prompt-panel${simple ? ' apps-prompt-panel--simple' : ''}`}>
      <div className="apps-prompt-panel__intro">
        <div>
          <span className="apps-setup-eyebrow">Startup screen checks</span>
          <h4>{simple ? 'Teach Suitestream what it may see' : 'Prompt Detection and Dismissal'}</h4>
          <p>
            {simple
              ? 'Save the normal ready screen first. You can also save profile pickers, Skip buttons, or other screens that block startup.'
              : 'Capture profile pickers, resume dialogs, offered Skip controls, and similar prompts. This step only tests recognition; it will not press OK or dismiss anything.'}
          </p>
        </div>
        <div className="apps-prompt-panel__capture-actions">
          {simple && (
            <button
              type="button"
              className="primary"
              onClick={() => handleCapture('ready_screen')}
              disabled={disabled || Boolean(busy)}
            >
              {busy === 'capture' ? 'Capturing…' : 'Save Current Screen as Ready'}
            </button>
          )}
          <button
            type="button"
            className={simple ? '' : 'primary'}
            onClick={() => handleCapture(simple ? 'profile_picker' : null)}
            disabled={disabled || Boolean(busy)}
          >
            {busy === 'capture' ? 'Capturing…' : simple ? 'Save a Startup Obstacle' : 'Capture Current Prompt'}
          </button>
        </div>
      </div>

      {!simple && rules.some((rule) => rule.response?.steps?.length > 0) && (
        <div className="apps-prompt-watch">
          <div>
            <strong>
              {hasAutomaticRules ? 'Automatic observation status' : 'Manual observation window'}
            </strong>
            <p>
              {hasAutomaticRules
                ? 'Launch this app normally to start a bounded watch using the enabled rules.'
                : 'Poll using each saved rule’s timing, respond only to confident matches, '
                  + 'and keep watching for consecutive prompts within the configured limits.'}
            </p>
          </div>
          {activeRun?.status === 'running' ? (
            <button type="button" onClick={handleStopWatch} disabled={Boolean(busy)}>
              {busy === 'stop-watch' ? 'Stopping…' : 'Stop Watching'}
            </button>
          ) : !hasAutomaticRules ? (
            <button
              type="button"
              className="primary"
              onClick={handleStartWatch}
              disabled={disabled || Boolean(busy)}
            >
              {busy === 'start-watch' ? 'Starting…' : 'Watch for Prompts'}
            </button>
          ) : (
            <span className="apps-prompt-watch__automatic">
              Automatic handling enabled
            </span>
          )}
          {activeRun && (
            <div className={`apps-prompt-watch__status ${activeRun.status}`}>
              <strong>{activeRun.status.replace(/_/g, ' ')}</strong>
              <span>
                {activeRun.trigger?.replace(/_/g, ' ') || 'manual'}
                {' '}·
                {activeRun.result?.checks || 0} checks
                {' '}· {activeRun.result?.screenshotRequests || 0} captures
                {' '}· {activeRun.result?.actions || 0} actions
                {' '}· {activeRun.result?.occurrences?.length || 0} occurrences
              </span>
              {activeRun.result?.lastCheck && (
                <small>
                  Last: {activeRun.result.lastCheck.ruleName}
                  {' '}at {Math.round((activeRun.result.lastCheck.confidence || 0) * 100)}%
                </small>
              )}
              {activeRun.result?.lastTransportError && (
                <small>Connection: {activeRun.result.lastTransportError}</small>
              )}
              {activeRun.status === 'running' && activeRun.result?.waitingForPackage && (
                <small>
                  Waiting for {activeRun.result.waitingForPackage}
                  {activeRun.result.observedForegroundPackage
                    ? `; currently ${activeRun.result.observedForegroundPackage}`
                    : ''}
                </small>
              )}
              {activeRun.result?.stopReason === 'foreground_package_changed' && (
                <small>
                  Stopped because the foreground app changed
                  {activeRun.result.observedForegroundPackage
                    ? ` to ${activeRun.result.observedForegroundPackage}`
                    : ''}.
                </small>
              )}
              {activeRun.error && (
                <small>Error: {activeRun.error}</small>
              )}
            </div>
          )}
        </div>
      )}

      {capture && (
        <div className="apps-prompt-capture">
          <div className="apps-prompt-guidance">
            <strong>{simple ? 'Mark the part that identifies this screen' : 'Select the stable identifier'}</strong>
            <p>
              Draw a box around a stable title, logo, or button. Avoid moving video, countdowns,
              progress animation, and changing recommendations.
            </p>
          </div>
          <div
            className="apps-visual-template-selector"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            role="application"
            aria-label={`Select a stable prompt region for ${appLabel}`}
          >
            <img
              ref={imageRef}
              src={capture.dataUrl}
              alt={`Current ${appLabel} prompt`}
              draggable="false"
            />
            {selection && (
              <span
                className="apps-visual-template-selection"
                style={{
                  left: `${selection.x * 100}%`,
                  top: `${selection.y * 100}%`,
                  width: `${selection.width * 100}%`,
                  height: `${selection.height * 100}%`,
                }}
              />
            )}
          </div>
          {selectionWarning && <div className="apps-prompt-warning">{selectionWarning}</div>}
        </div>
      )}

      {(capture || form.id) && (
        <div className="apps-prompt-editor">
          <div className="apps-prompt-editor__grid">
            <label>
              <span>{simple ? 'Name this screen' : 'Prompt name'}</span>
              <input
                value={form.name}
                onChange={(event) => setForm((current) => ({
                  ...current,
                  name: event.target.value,
                }))}
                placeholder="YouTube Skip control"
                aria-invalid={!form.name.trim()}
              />
              {!form.name.trim() && (
                <small className="apps-prompt-field-warning">
                  Required: give this screen a name before saving.
                </small>
              )}
            </label>
            {(!simple || form.promptType !== 'ready_screen') && <label>
              <span>{simple ? 'What is stopping startup?' : 'Prompt type'}</span>
              <select
                value={form.promptType}
                onChange={(event) => applyPreset(event.target.value)}
              >
                {Object.entries(PROMPT_PRESETS)
                  .filter(([value]) => !simple || !['ready_screen', 'buffering_indicator'].includes(value))
                  .map(([value, preset]) => (
                  <option key={value} value={value}>{preset.label}</option>
                  ))}
              </select>
            </label>}
            {!simple && <label className="apps-prompt-editor__wide">
              <span>Optional accessibility text, separated by commas</span>
              <input
                value={form.accessibilityValues}
                onChange={(event) => setForm((current) => ({
                  ...current,
                  accessibilityValues: event.target.value,
                }))}
                placeholder="skip, skip ad, choose profile"
              />
            </label>}
            {!simple && <label>
              <span>Visual match threshold</span>
              <span>{Math.round(form.decision.minimumConfidence * 100)}%</span>
              <input
                type="range"
                min="0.5"
                max="0.99"
                step="0.01"
                value={form.decision.minimumConfidence}
                onChange={(event) => setForm((current) => ({
                  ...current,
                  decision: {
                    ...current.decision,
                    minimumConfidence: Number(event.target.value),
                  },
                }))}
              />
            </label>}
            {!simple && <label>
              <span>Wait before first check</span>
              <span className="apps-timing-input">
                <input
                  type="number"
                  min="0"
                  max="30000"
                  step="500"
                  value={form.observation.initialDelayMs}
                  onChange={(event) => updateObservation('initialDelayMs', event.target.value)}
                />
                ms
              </span>
            </label>}
            {!simple && <label>
              <span>Check every</span>
              <span className="apps-timing-input">
                <input
                  type="number"
                  min="500"
                  max="10000"
                  step="500"
                  value={form.observation.intervalMs}
                  onChange={(event) => updateObservation('intervalMs', event.target.value)}
                />
                ms
              </span>
            </label>}
            {!simple && <label>
              <span>Observation window</span>
              <span className="apps-timing-input">
                <input
                  type="number"
                  min="1000"
                  max="120000"
                  step="1000"
                  value={form.observation.windowMs}
                  onChange={(event) => updateObservation('windowMs', event.target.value)}
                />
                ms
              </span>
            </label>}
            {!simple && <label>
              <span>Watch after a future response</span>
              <span className="apps-timing-input">
                <input
                  type="number"
                  min="0"
                  max="60000"
                  step="1000"
                  value={form.observation.continueAfterResponseMs}
                  onChange={(event) => updateObservation(
                    'continueAfterResponseMs',
                    event.target.value
                  )}
                />
                ms
              </span>
            </label>}
            {!simple && <label>
              <span>Maximum occurrences</span>
              <input
                type="number"
                min="1"
                max="5"
                value={form.observation.maxOccurrences}
                onChange={(event) => updateObservation('maxOccurrences', event.target.value)}
              />
            </label>}
          </div>
          {!simple && <fieldset className="apps-prompt-triggers">
            <legend>Test this pattern after</legend>
            {[
              ['after_package_launch', 'App launch'],
              ['after_intent_launch', 'Launch with intent/link'],
              ['after_prompt_response', 'Another prompt was handled'],
              ['manual', 'Manual checks'],
            ].map(([value, label]) => (
              <label key={value}>
                <input
                  type="checkbox"
                  checked={form.triggers.includes(value)}
                  onChange={() => toggleTrigger(value)}
                />
                {label}
              </label>
            ))}
          </fieldset>}
          <label className="apps-prompt-automation-toggle">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(event) => setForm((current) => ({
                ...current,
                enabled: event.target.checked,
              }))}
              disabled={form.promptType === 'buffering_indicator'}
            />
            <span>
              <strong>{form.promptType === 'ready_screen' ? 'Use as a ready-screen check' : 'Handle this screen automatically'}</strong>
              <small>
                {form.promptType === 'buffering_indicator'
                  ? 'Buffering patterns are activated from Continuous Playback Health below.'
                    : form.promptType === 'ready_screen'
                    ? 'The launch recipe can finish when this screen is visible.'
                    : form.response.steps.length === 0
                      ? 'Turn this on now, then record at least one button below before saving.'
                      : 'When this screen appears during launch, send the recorded Google TV commands.'}
              </small>
            </span>
          </label>
          {form.promptType !== 'ready_screen' && <div className="apps-prompt-response-recorder">
            <strong>{simple ? 'What should Suitestream press?' : 'Recorded response'}</strong>
            <p>
              {simple
                ? 'Press the buttons below in order. They are sent through Google TV Remote and saved as the response.'
                : 'Choose whether the buttons control the TV, update the recorded response, or do both.'}
            </p>
            {!simple && <fieldset className="apps-prompt-response-mode">
              <legend>Button behaviour</legend>
              {[
                ['send_and_record', 'Send and record'],
                ['record_only', 'Record only'],
                ['send_only', 'Send only'],
              ].map(([value, label]) => (
                <label key={value}>
                  <input
                    type="radio"
                    name="prompt-response-record-mode"
                    value={value}
                    checked={responseRecordMode === value}
                    onChange={(event) => setResponseRecordMode(event.target.value)}
                  />
                  {label}
                </label>
              ))}
            </fieldset>}
            <div className="apps-prompt-response-controls">
              {workingActions.map((action) => (
                <button
                  key={action}
                  type="button"
                  onClick={() => handleRecordResponseStep(action)}
                  disabled={
                    disabled
                    || Boolean(busy)
                    || (responseRecordMode !== 'send_only' && form.response.steps.length >= 10)
                  }
                >
                  {busy === `record-response:${action}`
                      ? 'Sending…'
                    : `${simple ? 'Press' : responseRecordMode === 'send_and_record'
                      ? 'Send + record'
                      : responseRecordMode === 'record_only'
                        ? 'Record'
                        : 'Send'} ${action.replace(/_/g, ' ')}`
                  }
                </button>
              ))}
            </div>
            {form.response.steps.length > 0 ? (
              <ol className="apps-prompt-response-steps">
                {form.response.steps.map((step, index) => (
                  <li key={`${step.key}-${index}`}>
                    <strong>{index + 1}. {step.key.replace(/_/g, ' ')}</strong>
                    <label>
                      <span>Wait after</span>
                      <span className="apps-timing-input">
                        <input
                          type="number"
                          min="0"
                          max="10000"
                          step="100"
                          value={step.delayAfterMs}
                          onChange={(event) => handleResponseDelay(index, event.target.value)}
                        />
                        ms
                      </span>
                    </label>
                    <button type="button" onClick={() => handleRemoveResponseStep(index)}>
                      Remove
                    </button>
                  </li>
                ))}
              </ol>
            ) : (
              <span>No response recorded yet. Detection-only rules can still be saved and tested.</span>
            )}
          </div>}
          {form.visualTemplate && !capture && (
            <div className="apps-prompt-existing-template">
              <img src={form.visualTemplate.templateDataUrl} alt={`${form.name} saved crop`} />
              <span>Saved visual crop. Capture again to replace it.</span>
            </div>
          )}
          <div className="apps-prompt-editor__actions">
            <button
              type="button"
              className="primary"
              onClick={handleSave}
              disabled={disabled || Boolean(busy)}
            >
              {busy === 'save'
                ? 'Saving…'
                : form.id
                  ? simple ? 'Save Screen Check Changes' : 'Save Detection Changes'
                  : simple ? 'Save Screen Check' : 'Save Detection Rule'}
            </button>
            <button
              type="button"
              onClick={() => {
                setForm(emptyForm(packageName));
                setCapture(null);
                setSelection(null);
              }}
              disabled={Boolean(busy)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {rules.length > 0 && (
        <div className="apps-prompt-rules">
          {rules.map((rule) => {
            const summary = detectorSummary(rule);
            const testResult = testResults[rule.id];
            return (
              <article key={rule.id}>
                <div className="apps-prompt-rule__summary">
                  {summary.visual?.template?.templateDataUrl && (
                    <img
                      src={summary.visual.template.templateDataUrl}
                      alt={`${rule.name} crop`}
                    />
                  )}
                  <span>
                    <strong>{rule.name}</strong>
                    <small>
                      {simple
                        ? rule.promptType === 'ready_screen' ? 'Ready screen' : 'Startup obstacle'
                        : `${summary.label || 'detector'} · every ${rule.observation.intervalMs}ms for ${Math.round(rule.observation.windowMs / 1000)}s`}
                    </small>
                    <small>
                      {simple
                        ? rule.promptType === 'ready_screen'
                          ? rule.enabled ? 'Available to launch recipes' : 'Not currently used'
                          : rule.enabled ? 'Handled automatically during launch' : 'Saved but not automatic'
                        : rule.enabled ? 'Automatic response enabled' : 'Manual-only rule'}
                    </small>
                  </span>
                </div>
                {testResult && (
                  <div className={testResult.matched ? 'apps-prompt-test passed' : 'apps-prompt-test'}>
                    <strong>
                      {testResult.testKind === 'response'
                        ? testResult.cleared
                          ? 'Response verified'
                          : testResult.actionSent
                            ? 'Response not cleared'
                            : 'Response withheld'
                        : testResult.matched
                          ? 'Matched'
                          : 'Not matched'}
                    </strong>
                    <span>
                      {testResult.testKind === 'response'
                        ? testResult.reason.replace(/_/g, ' ')
                        : `${Math.round(testResult.confidence * 100)}% confidence · no action sent`}
                    </span>
                    {(testResult.detectors || testResult.before?.detectors)?.map((detector) => (
                      <small key={detector.detectorId}>
                        {detector.type.replace(/_/g, ' ')}: {detector.reason.replace(/_/g, ' ')}
                      </small>
                    ))}
                  </div>
                )}
                <div className="apps-prompt-rule__actions">
                  <button
                    type="button"
                    className="primary"
                    onClick={() => handleTest(rule)}
                    disabled={disabled || Boolean(busy)}
                  >
                    {busy === `test:${rule.id}` ? 'Checking…' : simple ? 'Check Current Screen' : 'Test Detection'}
                  </button>
                  {rule.response?.steps?.length > 0 && (
                    <button
                      type="button"
                      onClick={() => handleTestResponse(rule)}
                      disabled={disabled || Boolean(busy)}
                    >
                      {busy === `test-response:${rule.id}` ? 'Trying response…' : simple ? 'Try Saved Response' : 'Test Response'}
                    </button>
                  )}
                  <button type="button" onClick={() => handleEdit(rule)} disabled={Boolean(busy)}>
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(rule)}
                    disabled={Boolean(busy)}
                  >
                    {busy === `delete:${rule.id}` ? 'Deleting…' : 'Delete'}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default PromptDetectionPanel;
